/**
 * Custom Filter Inputs
 *
 * Compact filter components (30px height, 0.78rem font) designed for use
 * inside CollapsibleFilterPanel. They wrap React Admin's standard inputs
 * with consistent sizing and styling.
 *
 * Available components:
 * - CustomTextInputFilter  — Text search / free-text filter
 * - CustomSelectInputFilter — Dropdown select filter
 * - CustomDateInputFilter  — Date picker filter
 *
 * All components accept the same props as their React Admin counterparts
 * (TextInputProps, SelectInputProps, DateInputProps) and merge custom sx.
 *
 * Usage:
 *   import { CustomTextInputFilter, CustomSelectInputFilter } from '@/components/common/CustomFilters';
 *
 *   <CollapsibleFilterPanel ...>
 *     <CustomTextInputFilter source="q" label="Search" alwaysOn />
 *     <CustomSelectInputFilter source="status" label="Status" choices={[...]} />
 *   </CollapsibleFilterPanel>
 */

import React from 'react';
import {
  TextInput,
  SelectInput,
  DateInput,
  TextInputProps,
  SelectInputProps,
  DateInputProps
} from 'react-admin';
import { SxProps } from '@mui/material';

/**
 * Shared styles for all filter inputs.
 * Overrides MUI defaults to produce a compact 30px-tall input
 * with properly centered labels and icons.
 */
const commonFilterStyles: SxProps = {
  // Responsive width: full width on mobile, auto on larger screens
  width: { xs: '100%', sm: 'auto' },

  '& .MuiInputBase-root': {
    height: '30px',
    minHeight: '30px !important',
    fontSize: '0.78rem',
    display: 'flex',
    alignItems: 'center',
    paddingRight: '10px',
  },

  '& .MuiInputLabel-root': {
    fontSize: '0.78rem',
    '&:not(.MuiInputLabel-shrink)': {
      transform: 'translate(14px, 5px) scale(1)',
    },
  },

  '& .MuiInputAdornment-root': {
    height: 'auto',
  },

  '& .MuiSelect-icon': {
    top: 'calc(50% - 12px)',
    right: '6px',
  },

  '& .MuiIconButton-root': {
    padding: '2px',
  },

  '& .MuiInputAdornment-positionEnd': {
    marginLeft: 0,
    height: '100%',
    maxHeight: '32px',
    '& .MuiIconButton-root': {
      padding: '4px',
    },
  },
};

// Custom TextInput filter
export const CustomTextInputFilter: React.FC<TextInputProps> = (props) => {
  return (
    <TextInput
      size="small"
      variant="outlined"
      helperText={false}
      {...props}
      sx={{ ...commonFilterStyles, ...props.sx }}
    />
  );
};

// Custom SelectInput filter
export const CustomSelectInputFilter: React.FC<SelectInputProps> = (props) => {
  return (
    <SelectInput
      size="small"
      variant="outlined"
      helperText={false}
      {...props}
      sx={{ ...commonFilterStyles, ...props.sx }}
    />
  );
};

// Custom DateInput filter
export const CustomDateInputFilter: React.FC<DateInputProps> = (props) => {
  return (
    <DateInput
      size="small"
      variant="outlined"
      helperText={false}
      {...props}
      sx={{ ...commonFilterStyles, ...props.sx }}
    />
  );
};
